// Order a Test, Part 12 — admin identity-linking visibility. Two views:
// (1) provider_registry — which clinician accounts (different practices)
//     belong to the same real physician, linked by NPI. Visibility only —
//     never implies shared data access between those accounts.
// (2) clinician_patient_records — a clinician's own patients and whether
//     each has been linked to a real umbrella identity_id yet.
//     2026-10-21 fix (issue checklist #11) — linked_identity_id is now
//     actually written, the first time a report is ever archived for a
//     given record (see labariumaireport/backend/main.py's
//     _link_clinician_patient_identity_once); before this it was never
//     set anywhere, so every row showed "Not yet linked" regardless. A
//     row shows "Not yet linked" now only when truly no report has been
//     archived against it yet, not as a standing gap. Deliberately
//     admin-only — the clinician who owns this record never sees this
//     field in their own API responses (labariumaiportal strips it).
//
// 2026-10-08 — visual pass, reported directly ("the UI sucks... not
// compatible with the others at all"): this page was a bare generic-MUI
// layout (plain white strip, no brand navbar, Paper-wrapped tables) while
// AdminDashboard.js/AuditDashboard.js/RcmDashboard.js all carry the same
// navy brand navbar + logo + bordered card/table styling. Brought this
// one in line rather than leaving it the odd one out.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { extractErrorDetail } from "./validators";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Chip, CircularProgress, Alert, Container, TextField, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip,
  Select, MenuItem,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import axios from "axios";
import AdminPageShell from "./AdminPageShell";
import { formatEST } from "../dateUtils";
import { PortalUserRecoveryActions } from "./UserDashboard";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000";
const BRAND_NAVY = "#00357a";

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("admin_token")}` };
}

// 2026-10-23 (Piece 5, ui-ux-checker finding) — the backend's raw NPPES
// outcome enum (same values _check_npi_against_nppes/npi_verification
// produce) was rendered to the admin verbatim, unlike its sibling
// identity_status chip in the same row, which already gets a proper
// human-readable mapping.
const NPI_OUTCOME_LABELS = {
  matched: "Matched",
  name_mismatch: "Name Mismatch",
  no_match: "Not Found",
  unavailable: "Unavailable",
};

// 2026-10-22 (Piece 2, one-identity-one-email invariant) — the claim-edit
// dialog for a single search-result row. Separate component (not inline
// in the map) so its own open/submitting/error state doesn't re-render
// every row on every keystroke.
function EditClaimDialog({ identityId, currentEmail, onClose, onChanged }) {
  const [newEmail, setNewEmail] = useState(currentEmail || "");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setSubmitting(true); setError(null);
    try {
      await axios.put(
        `${API}/api/admin/identity-linking/${encodeURIComponent(identityId)}/claim`,
        { new_email: newEmail.trim(), reason: reason.trim() },
        { headers: authHeaders() },
      );
      onChanged();
    } catch (err) {
      setError(extractErrorDetail(err, "Could not change this identity's email claim."));
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(newEmail.trim()) && reason.trim();

  return (
    <Dialog open onClose={() => !submitting && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, color: BRAND_NAVY }}>Edit Identity Email Claim</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Alert severity="info" sx={{ mb: 2 }}>
          If this identity has a linked patient account with a different login email, this will be rejected —
          change that account's login email instead, which updates this claim automatically.
        </Alert>
        <TextField
          label="New claimed email" type="email" fullWidth sx={{ mb: 2, mt: 0.5 }}
          value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
          helperText={currentEmail ? `Currently claimed: ${currentEmail}` : "Not yet claimed"}
        />
        <TextField
          label="Reason for change" required fullWidth multiline minRows={2}
          value={reason} onChange={(e) => setReason(e.target.value)}
          helperText="Required — recorded in the audit log."
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button onClick={submit} variant="contained" disabled={submitting || !canSubmit} sx={{ bgcolor: BRAND_NAVY }}>
          {submitting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// 2026-10-27 (identity-correction plan, Piece 3) — in-place correction of
// a patient identity's name/DOB/address, for a real lab typo or a
// genuine change the patient can't fix themselves (re-registering is
// blocked by the one-identity-one-account invariant). Deliberately
// separate from EditClaimDialog above (that one only ever touches which
// EMAIL is claimed) — this one changes the identity's own core fields,
// with its own collision-refusal behavior surfaced as a hard error.
function CorrectIdentityDialog({ identityId, currentFirstName, currentLastName, currentDob, onClose, onChanged }) {
  const [firstName, setFirstName] = useState(currentFirstName || "");
  const [lastName, setLastName] = useState(currentLastName || "");
  const [dob, setDob] = useState(currentDob || "");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressUnit, setAddressUnit] = useState("");
  const [addressCity, setAddressCity] = useState("");
  const [addressState, setAddressState] = useState("");
  const [addressZip, setAddressZip] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setSubmitting(true); setError(null);
    try {
      await axios.put(
        `${API}/api/admin/identity-linking/${encodeURIComponent(identityId)}/correct`,
        {
          first_name: firstName.trim(), last_name: lastName.trim(), dob: dob.trim(),
          address_line1: addressLine1.trim(), address_unit: addressUnit.trim(),
          address_city: addressCity.trim(), address_state: addressState.trim(), address_zip: addressZip.trim(),
          reason: reason.trim(),
        },
        { headers: authHeaders() },
      );
      onChanged();
    } catch (err) {
      setError(extractErrorDetail(err, "Could not correct this identity."));
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = firstName.trim() && lastName.trim() && dob.trim() && reason.trim();

  return (
    <Dialog open onClose={() => !submitting && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, color: BRAND_NAVY }}>Correct Identity Details</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Alert severity="info" sx={{ mb: 2 }}>
          This corrects the patient's own name/DOB/address in place — their report history and delivery
          folder are unaffected. If the corrected name and DOB already belong to a different existing
          identity, this will be rejected as a likely data-entry mix-up between two different people.
        </Alert>
        <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
          <TextField label="First name" required fullWidth value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          <TextField label="Last name" required fullWidth value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </Box>
        <TextField
          label="Date of birth" required fullWidth type="date" sx={{ mb: 2 }}
          InputLabelProps={{ shrink: true }} value={dob} onChange={(e) => setDob(e.target.value)}
        />
        <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#94a3b8", mb: 1 }}>
          Address (optional — leave blank to leave unchanged)
        </Typography>
        <TextField label="Address line 1" fullWidth sx={{ mb: 1.5 }} value={addressLine1} onChange={(e) => setAddressLine1(e.target.value)} />
        <Box sx={{ display: "flex", gap: 1.5, mb: 1.5 }}>
          <TextField label="Unit" fullWidth value={addressUnit} onChange={(e) => setAddressUnit(e.target.value)} />
          <TextField label="City" fullWidth value={addressCity} onChange={(e) => setAddressCity(e.target.value)} />
        </Box>
        <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
          <TextField label="State" fullWidth value={addressState} onChange={(e) => setAddressState(e.target.value)} />
          <TextField label="ZIP" fullWidth value={addressZip} onChange={(e) => setAddressZip(e.target.value)} />
        </Box>
        <TextField
          label="Reason for correction" required fullWidth multiline minRows={2}
          value={reason} onChange={(e) => setReason(e.target.value)}
          helperText="Required — recorded in the audit log."
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button onClick={submit} variant="contained" disabled={submitting || !canSubmit} sx={{ bgcolor: BRAND_NAVY }}>
          {submitting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Save Correction"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// 2026-10-23 (Piece 5, clinician invite-only build) — confirm dialog for
// releasing a completed clinician's identity claim back to pending.
// Separate component for the same reason EditClaimDialog is — isolated
// submitting/error state, no full-page re-render per keystroke (though
// this dialog has no text input, keeping the same convention/shape as
// its sibling for consistency).
function ReleaseClaimDialog({ sub, email, onClose, onReleased }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setSubmitting(true); setError(null);
    try {
      await axios.put(
        `${API}/api/admin/identity-linking/clinicians/${encodeURIComponent(sub)}/release-claim`,
        {}, { headers: authHeaders() },
      );
      onReleased();
    } catch (err) {
      setError(extractErrorDetail(err, "Could not release this identity claim."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={() => !submitting && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, color: BRAND_NAVY }}>Release Identity Claim</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Alert severity="warning">
          This clears <strong>{email || sub}</strong>'s name, practice, NPI, and phone, and sends their account
          back to the first-login setup gate. Use this only to resolve a genuine identity conflict in the
          rightful owner's favor — the released account will need to re-enter their details.
        </Alert>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button onClick={submit} variant="contained" color="warning" disabled={submitting}>
          {submitting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Release Claim"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function IdentityLinking() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [providers, setProviders] = useState([]);
  const [patients, setPatients] = useState([]);
  const [allIdentities, setAllIdentities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // 2026-10-22 (Piece 2, one-identity-one-email invariant) — search state.
  const [searchForm, setSearchForm] = useState({ email: "", first_name: "", last_name: "", dob: "" });
  const [searchResults, setSearchResults] = useState(null); // null = not yet searched
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [editingIdentityId, setEditingIdentityId] = useState(null); // identity_id currently in the Edit Claim dialog
  const [correctingIdentityId, setCorrectingIdentityId] = useState(null); // identity_id currently in the Correct Identity dialog
  // 2026-10-22 (Piece 2, ui-ux-checker finding) — editing a claim found via
  // an email search makes the matched row vanish on refresh (the new
  // email no longer matches the old query), which otherwise looks
  // identical to "nothing happened" with no success confirmation.
  const [claimEditNotice, setClaimEditNotice] = useState("");

  // 2026-10-23 (Piece 5, clinician invite-only build) — clinician identity
  // visibility + release-claim state.
  const [clinicians, setClinicians] = useState([]);
  const [cliniciansLoading, setCliniciansLoading] = useState(true);
  const [cliniciansError, setCliniciansError] = useState("");
  const [repFilter, setRepFilter] = useState("");
  const [releasingSub, setReleasingSub] = useState(null);
  const [releaseNotice, setReleaseNotice] = useState("");

  // 2026-10-26 addition (confirmed real gap, user-directed — "which
  // sales rep, how many clinicians, who invited who, track and filter")
  // — there was no roster of sales reps anywhere in this admin surface
  // at all, and the existing "Filter by inviter" box required already
  // knowing a rep's raw sub string. This table is that roster, each row
  // showing a real name + their own invited-clinician count, with a
  // one-click way to filter the Clinician Identities table below to
  // just that rep.
  const [salesReps, setSalesReps] = useState([]);
  const [salesRepsLoading, setSalesRepsLoading] = useState(true);
  const [salesRepsError, setSalesRepsError] = useState("");

  const fetchSalesReps = useCallback(async () => {
    setSalesRepsLoading(true); setSalesRepsError("");
    try {
      const res = await axios.get(`${API}/api/admin/identity-linking/sales-reps`, { headers: authHeaders() });
      if (res.data.error) setSalesRepsError(res.data.error);
      setSalesReps(res.data.sales_reps || []);
    } catch (err) {
      if (err?.response?.status === 401) { localStorage.removeItem("admin_token"); navigate("/admin-login"); return; }
      setSalesRepsError(extractErrorDetail(err, "Failed to load sales representatives."));
    } finally {
      setSalesRepsLoading(false);
    }
  }, [navigate]);

  // sub -> display name, for upgrading the Clinician Identities table's
  // "Invited By" column from a bare role string to a real name.
  const salesRepNameBySub = useMemo(() => {
    const m = {};
    for (const r of salesReps) m[r.sub] = r.name || r.email || r.sub;
    return m;
  }, [salesReps]);

  // 2026-10-26 fix (confirmed real gap, user-reported — "only hash is
  // coming") — clicking a rep's clinician count in the Sales
  // Representatives table above sets repFilter to that rep's raw `sub`
  // (an opaque id, necessarily — it's the actual API filter param, and
  // the pre-existing "Filter by inviter (sub)" box below is a legitimate
  // power-user field for admins who already know a sub), but nothing
  // ever told the admin WHICH rep that hash resolved to. Resolves it
  // back to a real name/email for display only — the filter itself
  // still runs on the raw sub underneath, unchanged.
  const filteredByRep = useMemo(
    () => (repFilter ? salesReps.find((r) => r.sub === repFilter) : null),
    [repFilter, salesReps],
  );

  // 2026-10-26 addition (user-directed — "sales rep segmented into labs
  // and filterable too") — reps are already lab_brand-scoped server-side
  // (assigned at creation, never client-editable); this makes that
  // segmentation visible/filterable here too, same client-side-filter
  // pattern as the rest of this already-fetched, small roster (no new
  // backend call needed).
  const [repLabBrandFilter, setRepLabBrandFilter] = useState("");
  const repLabBrandOptions = useMemo(
    () => Array.from(new Set(salesReps.map((r) => r.lab_brand).filter(Boolean))).sort(),
    [salesReps],
  );
  const filteredSalesReps = useMemo(
    () => (repLabBrandFilter ? salesReps.filter((r) => r.lab_brand === repLabBrandFilter) : salesReps),
    [salesReps, repLabBrandFilter],
  );

  const fetchClinicians = useCallback(async (invitedBySub) => {
    setCliniciansLoading(true); setCliniciansError("");
    try {
      const params = invitedBySub ? { invited_by_sub: invitedBySub } : {};
      const res = await axios.get(`${API}/api/admin/identity-linking/clinicians`, { params, headers: authHeaders() });
      if (res.data.error) setCliniciansError(res.data.error);
      setClinicians(res.data.clinicians || []);
    } catch (err) {
      if (err?.response?.status === 401) { localStorage.removeItem("admin_token"); navigate("/admin-login"); return; }
      setCliniciansError(extractErrorDetail(err, "Failed to load clinician identities."));
    } finally {
      setCliniciansLoading(false);
    }
  }, [navigate]);

  const runSearch = useCallback(async () => {
    const body = {
      email: searchForm.email.trim() || undefined,
      first_name: searchForm.first_name.trim() || undefined,
      last_name: searchForm.last_name.trim() || undefined,
      dob: searchForm.dob.trim() || undefined,
    };
    if (!body.email && !(body.first_name && body.last_name)) {
      setSearchError("Enter an email, or a first name and last name (date of birth optional).");
      return;
    }
    setSearchLoading(true); setSearchError(""); setClaimEditNotice("");
    try {
      const res = await axios.post(`${API}/api/admin/identity-linking/search`, body, { headers: authHeaders() });
      if (res.data.error) {
        // 2026-10-22 (Piece 2, ui-ux-checker finding) — a backend
        // misconfiguration error (e.g. PORTAL_MONGO_URI unset) used to
        // show BOTH the raw error text AND an empty "No matching
        // identity" table at the same time, which reads as contradictory.
        // Keep searchResults null (hides the table entirely) in this case.
        setSearchError(res.data.error);
        setSearchResults(null);
      } else {
        setSearchResults(res.data.identities || []);
      }
    } catch (err) {
      if (err?.response?.status === 401) { localStorage.removeItem("admin_token"); navigate("/admin-login"); return; }
      setSearchError(extractErrorDetail(err, "Identity search failed."));
      setSearchResults(null);
    } finally {
      setSearchLoading(false);
    }
  }, [searchForm, navigate]);

  const fetchAll = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [pvRes, ptRes, aiRes] = await Promise.all([
        axios.get(`${API}/api/admin/identity-linking/providers`, { headers: authHeaders() }),
        axios.get(`${API}/api/admin/identity-linking/patients`, { headers: authHeaders() }),
        axios.get(`${API}/api/admin/identity-linking/all-identities`, { headers: authHeaders() }),
      ]);
      if (pvRes.data.error || ptRes.data.error || aiRes.data.error) setError(pvRes.data.error || ptRes.data.error || aiRes.data.error);
      setProviders(pvRes.data.providers || []);
      setPatients(ptRes.data.patients || []);
      setAllIdentities(aiRes.data.identities || []);
    } catch (err) {
      if (err?.response?.status === 401) { localStorage.removeItem("admin_token"); navigate("/admin-login"); return; }
      setError(extractErrorDetail(err, "Failed to load identity-linking data."));
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem("admin_token")) { navigate("/admin-login"); return; }
    fetchAll();
    fetchClinicians();
    fetchSalesReps();
  }, [fetchAll, fetchClinicians, fetchSalesReps, navigate]);

  return (
    <AdminPageShell
      isDark={isDark}
      backPath="/admin"
      title="Identity Linking"
      subtitle="Admin visibility only — provider/practice links and clinician-patient umbrella-identity status"
    >
      <Container maxWidth="md">
        {error && <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

        {/* 2026-10-22 (Piece 2, one-identity-one-email invariant) — search
            by claimed email, or by first+last name (DOB optional). Exact
            hash match only, same discipline as every identity-resolution
            lookup in this codebase — no fuzzy/partial scan. */}
        <Box sx={{
          backgroundColor: isDark ? "#111827" : "#FFF",
          border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid #E8E8E8",
          borderRadius: 2, p: 3, mb: 3,
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
            <Box sx={{ width: 3, height: 18, backgroundColor: BRAND_NAVY, borderRadius: 1 }} />
            <Typography sx={{ fontWeight: 600, fontSize: "0.95rem", color: isDark ? "#f1f5f9" : "#111" }}>Search Identity Email Claims</Typography>
          </Box>
          <Typography sx={{ fontSize: "0.8rem", color: isDark ? "#94a3b8" : "#888", mb: 2, ml: "18px" }}>
            Exact match only — an email, or a first name and last name (date of birth optional).
          </Typography>
          <Box component="form" onSubmit={(e) => { e.preventDefault(); runSearch(); }}
            sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-end", mb: searchError ? 2 : 0 }}>
            <TextField size="small" label="Email" value={searchForm.email}
              onChange={(e) => setSearchForm({ ...searchForm, email: e.target.value })} sx={{ minWidth: 220 }} />
            <Typography sx={{ color: "#AAA", fontSize: "0.8rem", pb: 1 }}>or</Typography>
            <TextField size="small" label="First name" value={searchForm.first_name}
              onChange={(e) => setSearchForm({ ...searchForm, first_name: e.target.value })} sx={{ minWidth: 150 }} />
            <TextField size="small" label="Last name" value={searchForm.last_name}
              onChange={(e) => setSearchForm({ ...searchForm, last_name: e.target.value })} sx={{ minWidth: 150 }} />
            <TextField size="small" label="DOB (optional)" placeholder="YYYY-MM-DD" value={searchForm.dob}
              onChange={(e) => setSearchForm({ ...searchForm, dob: e.target.value })} sx={{ minWidth: 150 }} />
            <Button type="submit" variant="contained" disabled={searchLoading} sx={{ bgcolor: BRAND_NAVY, textTransform: "none", fontWeight: 700 }}>
              {searchLoading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Search"}
            </Button>
          </Box>
          {searchError && <Alert severity="warning" sx={{ mt: 1 }}>{searchError}</Alert>}
          {claimEditNotice && <Alert severity="success" sx={{ mt: 1 }}>{claimEditNotice}</Alert>}

          {searchResults !== null && (
            <TableContainer sx={{ mt: 2, border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid #E8E8E8", borderRadius: 2, overflowX: "auto", overflowY: "hidden" }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "#F7F7F8" }}>
                    {["Patient", "DOB", "Claimed Email", "Claimed Via", "Claimed At", "Linked Account", "Actions"].map((h) => (
                      <TableCell key={h} sx={{ fontSize: "0.63rem", fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: isDark ? "#94a3b8" : "#555", borderBottom: isDark ? "2px solid rgba(255,255,255,0.1)" : "2px solid #E4E4E7" }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {searchResults.length === 0 ? (
                    <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: "#AAA", fontSize: "0.82rem" }}>No matching identity.</TableCell></TableRow>
                  ) : (
                    searchResults.map((r) => (
                      <TableRow key={r.identity_id} sx={{ "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#FAFAFA" } }}>
                        <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5", fontSize: "0.85rem", color: isDark ? "#e5e7eb" : "inherit" }}>
                          {(r.patient_first_name || r.patient_last_name) ? `${r.patient_first_name} ${r.patient_last_name}`.trim() : <span style={{ color: "#AAA" }}>No portal account</span>}
                        </TableCell>
                        <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5", fontSize: "0.8rem", color: isDark ? "#94a3b8" : "#666" }}>{r.patient_dob || "—"}</TableCell>
                        <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5", fontSize: "0.8rem", color: isDark ? "#e5e7eb" : "#111" }}>
                          {r.claimed_email || <span style={{ color: "#AAA" }}>Not yet claimed</span>}
                        </TableCell>
                        <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5", fontSize: "0.8rem", color: isDark ? "#94a3b8" : "#666" }}>{r.claimed_via_flow || "—"}</TableCell>
                        <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5", fontSize: "0.8rem", color: isDark ? "#94a3b8" : "#666" }}>
                          {r.claimed_at ? formatEST(r.claimed_at, { dateOnly: true }) : "—"}
                        </TableCell>
                        <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5" }}>
                          {r.linked_account_sub ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                              <Box>
                                <Typography sx={{ fontSize: "0.8rem", color: isDark ? "#e5e7eb" : "#111" }}>{r.linked_account_email}</Typography>
                                {/* 2026-10-22 (Piece 2, ui-ux-checker finding) — the
                                    whole point of this page is spotting claim/
                                    login-email drift; without this flag an admin
                                    would have to open the dialog below to notice
                                    the two differ. */}
                                {r.claimed_email && r.linked_account_email && r.claimed_email !== r.linked_account_email && (
                                  <Chip size="small" label="Differs from claim" color="warning" variant="outlined" sx={{ fontSize: "0.63rem", height: 18, mt: 0.25 }} />
                                )}
                              </Box>
                              <PortalUserRecoveryActions sub={r.linked_account_sub} email={r.linked_account_email} onChanged={runSearch} />
                            </Box>
                          ) : (
                            <span style={{ color: "#AAA", fontSize: "0.8rem" }}>No linked account</span>
                          )}
                        </TableCell>
                        <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5" }}>
                          <Tooltip title="Edit email claim">
                            <IconButton size="small" onClick={() => setEditingIdentityId(r.identity_id)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Correct name/DOB/address">
                            <IconButton size="small" onClick={() => setCorrectingIdentityId(r.identity_id)}>
                              <PersonSearchIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        {editingIdentityId && (
          <EditClaimDialog
            identityId={editingIdentityId}
            currentEmail={(searchResults || []).find((r) => r.identity_id === editingIdentityId)?.claimed_email || ""}
            onClose={() => setEditingIdentityId(null)}
            onChanged={async () => {
              setEditingIdentityId(null);
              await runSearch();
              setClaimEditNotice("Email claim updated. If this was the only match for your search, it may no longer appear below.");
            }}
          />
        )}

        {correctingIdentityId && (
          <CorrectIdentityDialog
            identityId={correctingIdentityId}
            currentFirstName={(searchResults || []).find((r) => r.identity_id === correctingIdentityId)?.patient_first_name || ""}
            currentLastName={(searchResults || []).find((r) => r.identity_id === correctingIdentityId)?.patient_last_name || ""}
            currentDob={(searchResults || []).find((r) => r.identity_id === correctingIdentityId)?.patient_dob || ""}
            onClose={() => setCorrectingIdentityId(null)}
            onChanged={async () => {
              setCorrectingIdentityId(null);
              await runSearch();
              setClaimEditNotice("Identity corrected. Their report history is unaffected — the search above reflects the corrected details.");
            }}
          />
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress sx={{ color: BRAND_NAVY }} /></Box>
        ) : (
          <>
            <Box sx={{
              backgroundColor: isDark ? "#111827" : "#FFF",
              border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid #E8E8E8",
              borderRadius: 2, p: 3, mb: 3,
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
                <Box sx={{ width: 3, height: 18, backgroundColor: BRAND_NAVY, borderRadius: 1 }} />
                <Typography sx={{ fontWeight: 600, fontSize: "0.95rem", color: isDark ? "#f1f5f9" : "#111" }}>Same Physician, Multiple Practices</Typography>
              </Box>
              <Typography sx={{ fontSize: "0.8rem", color: isDark ? "#94a3b8" : "#888", mb: 2, ml: "18px" }}>
                Clinician accounts sharing an NPI across different practices — visibility only, never shared data access
                between the accounts below.
              </Typography>
              {providers.length === 0 ? (
                <Typography sx={{ color: "#AAA", fontSize: "0.82rem", textAlign: "center", py: 3 }}>No physician is currently linked across multiple practices.</Typography>
              ) : (
                <TableContainer sx={{ border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid #E8E8E8", borderRadius: 2, overflowX: "auto", overflowY: "hidden" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "#F7F7F8" }}>
                        {["NPI", "Linked Accounts"].map((h) => (
                          <TableCell key={h} sx={{ fontSize: "0.63rem", fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: isDark ? "#94a3b8" : "#555", borderBottom: isDark ? "2px solid rgba(255,255,255,0.1)" : "2px solid #E4E4E7" }}>
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {providers.map((p) => (
                        <TableRow key={p.npi} sx={{ "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#FAFAFA" } }}>
                          <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5", fontFamily: "monospace", fontSize: "0.8rem", color: isDark ? "#e5e7eb" : "inherit" }}>{p.npi}</TableCell>
                          <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5" }}>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                              {p.linked_accounts.map((a, i) => (
                                <Chip key={i} size="small"
                                  label={`${a.first_name} ${a.last_name} — ${a.practice_name}`}
                                  sx={{ backgroundColor: isDark ? "rgba(0,53,122,0.25)" : "#F0F4FF", color: isDark ? "#9db9e8" : "#1A3A7D", fontSize: "0.68rem", height: 20, borderRadius: 1 }} />
                              ))}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>

            <Box sx={{
              backgroundColor: isDark ? "#111827" : "#FFF",
              border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid #E8E8E8",
              borderRadius: 2, p: 3,
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
                <Box sx={{ width: 3, height: 18, backgroundColor: BRAND_NAVY, borderRadius: 1 }} />
                <Typography sx={{ fontWeight: 600, fontSize: "0.95rem", color: isDark ? "#f1f5f9" : "#111" }}>Clinician Patient Records → Umbrella Identity</Typography>
              </Box>
              <Typography sx={{ fontSize: "0.8rem", color: isDark ? "#94a3b8" : "#888", mb: 2, ml: "18px" }}>
                Every patient a clinician has entered via Order a Test, and whether it's been linked to a real
                Labarium patient identity yet. Linking now happens as soon as the patient is added or resolved
                {/* 2026-10-22 (Piece 6, final-review wording fix) — this used to say "when a submitted order is
                    fulfilled," true before Piece 6 made Order a Test link the identity immediately on patient
                    creation/resolve instead of waiting for first-report-archival. */}
                , not only once a report comes back.
              </Typography>
              <TableContainer sx={{ border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid #E8E8E8", borderRadius: 2, overflowX: "auto", overflowY: "hidden" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "#F7F7F8" }}>
                      {["Patient", "DOB", "Address", "Contact", "Practice", "Updated", "Umbrella Identity"].map((h) => (
                        <TableCell key={h} sx={{ fontSize: "0.63rem", fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: isDark ? "#94a3b8" : "#555", borderBottom: isDark ? "2px solid rgba(255,255,255,0.1)" : "2px solid #E4E4E7" }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {patients.length === 0 ? (
                      <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: "#AAA", fontSize: "0.82rem" }}>No clinician patient records yet.</TableCell></TableRow>
                    ) : (
                      patients.map((p) => (
                        <TableRow key={p.id} sx={{ "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#FAFAFA" } }}>
                          <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5", fontSize: "0.85rem", color: isDark ? "#e5e7eb" : "inherit" }}>{p.first_name} {p.last_name}</TableCell>
                          <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5", fontSize: "0.8rem", color: isDark ? "#94a3b8" : "#666" }}>{p.dob}</TableCell>
                          <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5", fontSize: "0.8rem", color: isDark ? "#94a3b8" : "#666" }}>
                            {[p.address_line1, p.address_unit, p.address_city, p.address_state, p.address_zip].filter(Boolean).join(", ") || "—"}
                          </TableCell>
                          {/* 2026-10-21 fix (issue checklist #30) — backend already
                              decrypts/returns email+phone on this record; the
                              table just never rendered them. */}
                          <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5", fontSize: "0.8rem", color: isDark ? "#94a3b8" : "#666" }}>
                            {p.email || "—"}{p.phone ? ` · ${p.phone}` : ""}
                          </TableCell>
                          <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5", fontSize: "0.8rem", color: isDark ? "#94a3b8" : "#666" }}>{p.practice_name}</TableCell>
                          <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5", fontSize: "0.8rem", color: isDark ? "#94a3b8" : "#666" }}>
                            {p.updated_at ? formatEST(p.updated_at, { dateOnly: true }) : "—"}
                          </TableCell>
                          <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5" }}>
                            {p.linked_identity_id ? (
                              <Chip size="small" label={p.linked_identity_id} color="success" variant="outlined" sx={{ fontFamily: "monospace" }} />
                            ) : (
                              <Chip size="small" label="Not yet linked" sx={{ backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "#F4F4F5", color: isDark ? "#94a3b8" : "#71717a" }} />
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* 2026-10-14 fix — real gap, reported directly: the two
                sections above only ever covered identities created through
                a clinician's Order-a-Test flow. Identities created by a
                direct lab upload or a patient/clinician self-registering
                had ZERO admin visibility anywhere, despite both writing
                into this same shared identity_index collection. */}
            <Box sx={{
              backgroundColor: isDark ? "#111827" : "#FFF",
              border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid #E8E8E8",
              borderRadius: 2, p: 3, mt: 3,
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
                <Box sx={{ width: 3, height: 18, backgroundColor: BRAND_NAVY, borderRadius: 1 }} />
                <Typography sx={{ fontWeight: 600, fontSize: "0.95rem", color: isDark ? "#f1f5f9" : "#111" }}>All Patient Identities (direct lab upload / self-registration)</Typography>
              </Box>
              <Typography sx={{ fontSize: "0.8rem", color: isDark ? "#94a3b8" : "#888", mb: 2, ml: "18px" }}>
                Every record in the shared identity-matching system, including identities created outside the
                Order-a-Test flow (a direct lab report upload, or a patient/clinician registering on the portal
                directly). Patient Name/DOB are shown when a matching portal patient account exists (decrypted
                from that account's own record) — falling back to the storage-folder identifier when no such
                account exists yet. Matching itself still runs purely on the address HASH, never the literal
                value — the "Address" column below is an additional, separately-stored, admin-decryptable copy
                (2026-10-26 addition), shown only going forward for identities created/updated since; "—" means
                this identity predates it, not that something failed. "Addresses on File" is still the hash-list
                count, independent of whether a literal copy is also on file.
              </Typography>
              {/* 2026-10-26 fix (ui-ux-checker finding, real UI sweep) —
                  this table's rightmost column ("Created") was clipping
                  at the card edge with no visible scroll affordance.
                  overflowX:"auto" was already correct — the gap was
                  purely that the horizontal scrollbar is invisible by
                  default on most platforms until hovered, reading as a
                  hard clip rather than a scrollable area. Forcing it
                  always-visible (same navy/gold palette as the rest of
                  this page) makes the scrollability obvious. */}
              <TableContainer sx={{
                border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid #E8E8E8", borderRadius: 2, overflowX: "auto", overflowY: "hidden",
                "&::-webkit-scrollbar": { height: 8 },
                "&::-webkit-scrollbar-track": { backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#F0F0F0" },
                "&::-webkit-scrollbar-thumb": { backgroundColor: isDark ? "rgba(201,169,110,0.5)" : "rgba(0,53,122,0.3)", borderRadius: 4 },
              }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "#F7F7F8" }}>
                      {["Patient Name", "DOB", "Identity (name_dob_id)", "Identity ID", "Variant", "Address", "Addresses on File", "Reports", "Labs", "Report Types", "Created"].map((h) => (
                        <TableCell key={h} sx={{ fontSize: "0.63rem", fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: isDark ? "#94a3b8" : "#555", borderBottom: isDark ? "2px solid rgba(255,255,255,0.1)" : "2px solid #E4E4E7" }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allIdentities.length === 0 ? (
                      <TableRow><TableCell colSpan={11} align="center" sx={{ py: 4, color: "#AAA", fontSize: "0.82rem" }}>No identities yet.</TableCell></TableRow>
                    ) : (
                      allIdentities.map((i) => {
                        const hasPatientRecord = i.patient_first_name || i.patient_last_name;
                        return (
                        <TableRow key={i.identity_id} sx={{ "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#FAFAFA" } }}>
                          <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5", fontSize: "0.8rem", color: isDark ? "#e5e7eb" : "#111" }}>
                            {hasPatientRecord ? `${i.patient_first_name} ${i.patient_last_name}`.trim() : <span style={{ color: "#AAA" }}>No portal account</span>}
                          </TableCell>
                          <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5", fontSize: "0.8rem", color: isDark ? "#e5e7eb" : "#111" }}>
                            {i.patient_dob || "—"}
                          </TableCell>
                          <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5", fontSize: "0.8rem", fontFamily: "monospace", color: isDark ? "#94a3b8" : "#666" }}>{i.folder_identifier}</TableCell>
                          <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5", fontSize: "0.78rem", fontFamily: "monospace", color: isDark ? "#94a3b8" : "#666" }}>{i.identity_id}</TableCell>
                          <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5", fontSize: "0.8rem", color: isDark ? "#94a3b8" : "#666" }}>{i.variant}</TableCell>
                          <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5", fontSize: "0.78rem", color: isDark ? "#e5e7eb" : "#111", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={i.address || ""}>
                            {i.address || <span style={{ color: "#AAA" }}>—</span>}
                          </TableCell>
                          <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5", fontSize: "0.8rem", color: isDark ? "#94a3b8" : "#666" }}>{i.address_count}</TableCell>
                          <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5", fontSize: "0.8rem", color: isDark ? "#94a3b8" : "#666" }}>{i.accession_count}</TableCell>
                          <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5", fontSize: "0.8rem", color: isDark ? "#94a3b8" : "#666" }}>{(i.labs || []).join(", ") || "—"}</TableCell>
                          {/* 2026-10-18 fix (frontend-backend contract
                              sweep) — report_types was computed server-side
                              per identity but never rendered anywhere. */}
                          <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5", fontSize: "0.8rem", color: isDark ? "#94a3b8" : "#666" }}>{(i.report_types || []).join(", ") || "—"}</TableCell>
                          <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5", fontSize: "0.8rem", color: isDark ? "#94a3b8" : "#666" }}>
                            {i.created_at ? formatEST(i.created_at, { dateOnly: true }) : "—"}
                          </TableCell>
                        </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* 2026-10-26 addition (confirmed real gap, user-directed) —
                sales-rep roster: who they are, which lab brand, and how
                many clinicians each has invited. "View Clinicians"
                scrolls the filter below to just that rep. */}
            <Box sx={{
              backgroundColor: isDark ? "#111827" : "#FFF",
              border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid #E8E8E8",
              borderRadius: 2, p: 3, mt: 3,
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
                <Box sx={{ width: 3, height: 18, backgroundColor: BRAND_NAVY, borderRadius: 1 }} />
                <Typography sx={{ fontWeight: 600, fontSize: "0.95rem", color: isDark ? "#f1f5f9" : "#111" }}>Sales Representatives</Typography>
              </Box>
              <Typography sx={{ fontSize: "0.8rem", color: isDark ? "#94a3b8" : "#888", mb: 2, ml: "18px" }}>
                Every sales-rep account and how many clinicians they've personally invited. Click a row's count
                to filter the Clinician Identities table below to just that rep.
              </Typography>
              {repLabBrandOptions.length > 1 && (
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mb: 2 }}>
                  <Select size="small" displayEmpty value={repLabBrandFilter}
                    onChange={(e) => setRepLabBrandFilter(e.target.value)} sx={{ minWidth: 180 }}>
                    <MenuItem value="">All lab brands</MenuItem>
                    {repLabBrandOptions.map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                  </Select>
                  {repLabBrandFilter && (
                    <Button size="small" onClick={() => setRepLabBrandFilter("")} sx={{ textTransform: "none" }}>Clear</Button>
                  )}
                </Box>
              )}
              {salesRepsError && <Alert severity="warning" sx={{ mb: 2 }}>{salesRepsError}</Alert>}
              {salesRepsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress sx={{ color: BRAND_NAVY }} /></Box>
              ) : (
                <TableContainer sx={{ border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid #E8E8E8", borderRadius: 2, overflowX: "auto", overflowY: "hidden" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "#F7F7F8" }}>
                        {["Name", "Email", "Lab Brand", "Clinicians Invited", "Created"].map((h) => (
                          <TableCell key={h} sx={{ fontSize: "0.63rem", fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: isDark ? "#94a3b8" : "#555", borderBottom: isDark ? "2px solid rgba(255,255,255,0.1)" : "2px solid #E4E4E7" }}>
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredSalesReps.length === 0 ? (
                        <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: "#AAA", fontSize: "0.82rem" }}>
                          {salesReps.length === 0 ? "No sales reps yet." : "No sales reps match this lab brand."}
                        </TableCell></TableRow>
                      ) : (
                        filteredSalesReps.map((r) => (
                          <TableRow key={r.sub} sx={{ "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#FAFAFA" } }}>
                            <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5", fontSize: "0.8rem", color: isDark ? "#e5e7eb" : "#111" }}>{r.name || "—"}</TableCell>
                            <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5", fontSize: "0.8rem", color: isDark ? "#94a3b8" : "#666" }}>{r.email || "—"}</TableCell>
                            <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5", fontSize: "0.8rem", color: isDark ? "#94a3b8" : "#666" }}>{r.lab_brand || "—"}</TableCell>
                            <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5" }}>
                              <Button size="small" variant="outlined" onClick={() => { setRepFilter(r.sub); setReleaseNotice(""); fetchClinicians(r.sub); }}
                                sx={{ textTransform: "none", fontSize: "0.75rem", borderColor: BRAND_NAVY, color: BRAND_NAVY }}>
                                {r.invited_clinician_count}
                              </Button>
                            </TableCell>
                            <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5", fontSize: "0.8rem", color: isDark ? "#94a3b8" : "#666" }}>
                              {r.created_at ? formatEST(r.created_at, { dateOnly: true }) : "—"}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>

            {/* 2026-10-23 (Piece 5, clinician invite-only build) —
                clinician identity visibility + conflict resolution. */}
            <Box sx={{
              backgroundColor: isDark ? "#111827" : "#FFF",
              border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid #E8E8E8",
              borderRadius: 2, p: 3, mt: 3,
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
                <Box sx={{ width: 3, height: 18, backgroundColor: BRAND_NAVY, borderRadius: 1 }} />
                <Typography sx={{ fontWeight: 600, fontSize: "0.95rem", color: isDark ? "#f1f5f9" : "#111" }}>Clinician Identities</Typography>
              </Box>
              <Typography sx={{ fontSize: "0.8rem", color: isDark ? "#94a3b8" : "#888", mb: 2, ml: "18px" }}>
                Every invited clinician account — whether their profile (name/practice/NPI/signature) is
                complete yet, who invited them, and the NPI registry check outcome for a completed claim.
                "Release Claim" clears a completed clinician's identity and sends them back to the first-login
                setup gate — use it only to resolve a genuine conflict in the rightful owner's favor.
              </Typography>
              <Box component="form"
                onSubmit={(e) => { e.preventDefault(); setReleaseNotice(""); fetchClinicians(repFilter.trim() || undefined); }}
                sx={{ display: "flex", gap: 2, alignItems: "flex-end", mb: 2 }}>
                <TextField size="small" label="Filter by inviter (sub)" value={repFilter}
                  onChange={(e) => setRepFilter(e.target.value)} sx={{ minWidth: 260 }}
                  helperText="Admin or sales rep's sub — leave blank to show everyone" />
                <Button type="submit" variant="outlined"
                  sx={{ borderColor: BRAND_NAVY, color: BRAND_NAVY, textTransform: "none" }}>
                  Apply Filter
                </Button>
                {repFilter && (
                  <Button onClick={() => { setRepFilter(""); setReleaseNotice(""); fetchClinicians(); }} sx={{ textTransform: "none" }}>
                    Clear
                  </Button>
                )}
              </Box>
              {repFilter && (
                <Typography sx={{ fontSize: "0.78rem", color: isDark ? "#94a3b8" : "#666", mb: 2 }}>
                  {filteredByRep
                    ? <>Showing clinicians invited by <b>{filteredByRep.name || "—"}</b>{filteredByRep.email ? ` (${filteredByRep.email})` : ""}.</>
                    : "Showing clinicians invited by this sub — no matching rep found in the roster above (may be the admin's own sub, or a since-removed rep)."}
                </Typography>
              )}
              {cliniciansError && <Alert severity="warning" sx={{ mb: 2 }}>{cliniciansError}</Alert>}
              {releaseNotice && <Alert severity="success" sx={{ mb: 2 }}>{releaseNotice}</Alert>}
              {cliniciansLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress sx={{ color: BRAND_NAVY }} /></Box>
              ) : (
                <TableContainer sx={{ border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid #E8E8E8", borderRadius: 2, overflowX: "auto", overflowY: "hidden" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "#F7F7F8" }}>
                        {["Email", "Name", "Practice", "NPI", "NPI Check", "Status", "Invited By", "Invited At", "Actions"].map((h) => (
                          <TableCell key={h} sx={{ fontSize: "0.63rem", fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: isDark ? "#94a3b8" : "#555", borderBottom: isDark ? "2px solid rgba(255,255,255,0.1)" : "2px solid #E4E4E7" }}>
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {clinicians.length === 0 ? (
                        <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: "#AAA", fontSize: "0.82rem" }}>No clinician accounts yet.</TableCell></TableRow>
                      ) : (
                        clinicians.map((c) => (
                          <TableRow key={c.sub} sx={{ "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#FAFAFA" } }}>
                            <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5", fontSize: "0.8rem", color: isDark ? "#e5e7eb" : "#111" }}>{c.email || "—"}</TableCell>
                            <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5", fontSize: "0.8rem", color: isDark ? "#e5e7eb" : "#111" }}>
                              {(c.first_name || c.last_name) ? `${c.first_name || ""} ${c.last_name || ""}`.trim() : "—"}
                            </TableCell>
                            <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5", fontSize: "0.8rem", color: isDark ? "#94a3b8" : "#666" }}>{c.practice_name || "—"}</TableCell>
                            <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5", fontSize: "0.8rem", fontFamily: "monospace", color: isDark ? "#94a3b8" : "#666" }}>{c.npi || "—"}</TableCell>
                            <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5" }}>
                              {c.npi_verification_outcome ? (
                                <Chip size="small" label={NPI_OUTCOME_LABELS[c.npi_verification_outcome] || c.npi_verification_outcome}
                                  color={c.npi_verification_outcome === "matched" ? "success" : "warning"} variant="outlined" />
                              ) : "—"}
                            </TableCell>
                            <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5" }}>
                              <Chip size="small"
                                label={c.identity_status === "pending_profile" ? "Pending Setup" : "Complete"}
                                color={c.identity_status === "pending_profile" ? "default" : "success"}
                                variant={c.identity_status === "pending_profile" ? "filled" : "outlined"} />
                            </TableCell>
                            <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5", fontSize: "0.78rem", color: isDark ? "#94a3b8" : "#666" }}>
                              {/* 2026-10-26 fix (confirmed real gap) — used
                                  to show only the bare role string
                                  ("sales_rep"), never WHICH rep. Resolves
                                  the real name from the Sales Representatives
                                  table above when the inviter is a rep;
                                  falls back to the role alone for an admin
                                  inviter (no name list for admins here) or
                                  when the sub doesn't resolve. */}
                              {c.invited_by_role === "sales_rep"
                                ? (salesRepNameBySub[c.invited_by_sub] || "sales_rep")
                                : (c.invited_by_role || "—")}
                            </TableCell>
                            <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5", fontSize: "0.8rem", color: isDark ? "#94a3b8" : "#666" }}>
                              {c.created_at ? formatEST(c.created_at, { dateOnly: true }) : "—"}
                            </TableCell>
                            <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5" }}>
                              {c.identity_status === "complete" ? (
                                <Button size="small" color="warning" onClick={() => setReleasingSub(c.sub)} sx={{ textTransform: "none", fontSize: "0.75rem" }}>
                                  Release Claim
                                </Button>
                              ) : "—"}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>

            {releasingSub && (
              <ReleaseClaimDialog
                sub={releasingSub}
                email={(clinicians.find((c) => c.sub === releasingSub) || {}).email}
                onClose={() => setReleasingSub(null)}
                onReleased={async () => {
                  setReleasingSub(null);
                  await fetchClinicians(repFilter.trim() || undefined);
                  setReleaseNotice("Identity claim released — the account is back at the first-login setup gate.");
                }}
              />
            )}
          </>
        )}
      </Container>
    </AdminPageShell>
  );
}
